import type { FC } from 'react';
import { User, Rocket, CheckCircle, Target } from 'lucide-react';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { RadarChartComponent } from '../charts/RadarChartComponent';
import { Tabs } from '../ui/Tabs';
import { ActionPlanSection } from './ActionPlanSection';
import type { DevelopmentStrategy } from '../../types';

interface PremiumJourneyProps {
  result: any;
}

export const PremiumJourney: FC<PremiumJourneyProps> = ({ result }) => {
  if (!result || !result.report) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500">Carregando resultados ou nenhum resultado encontrado...</p>
      </div>
    );
  }

  const { report } = result;
  const { dominantProfileContent, radarChartData } = report;

  // Transforma os itens do plano de ação da API na estrutura que o Kanban espera.
  // Prioriza plano_acao_detalhado se disponível, senão usa formato legacy
  const actionPlanStrategies: DevelopmentStrategy[] = (() => {
    if (dominantProfileContent.plano_acao_detalhado && dominantProfileContent.plano_acao_detalhado.length > 0) {
      // Usa dados ricos do novo formato
      return dominantProfileContent.plano_acao_detalhado.map((item: any) => ({
        id: item.id,
        titulo: item.titulo,                    // Título correto da tarefa
        descricao: item.descricao,              // Descrição detalhada real
        categoria: item.categoria,              // Categoria real do backend
        prazo_implementacao: item.prazo,        // Prazo real do backend
        area_melhoria: item.area_melhoria,      // Área de melhoria
        complexidade: item.complexidade,        // Complexidade real
        periodo: item.periodo,                  // Período (30_dias, 90_dias, etc.)
        sugestoes: [],
        impacto: '',
      }));
    } else {
      // Fallback para formato legacy com melhor parsing
      return (dominantProfileContent.plano_acao_itens || []).map((item: string, index: number) => {
        // Tenta separar título da descrição se estiver no formato "titulo: descricao"
        const parts = item.split(': ');
        const titulo = parts[0] || item;
        const descricao = parts.length > 1 ? parts.slice(1).join(': ') : "Descrição detalhada a ser fornecida pelo backend.";

        return {
          id: `acao-${index + 1}`,
          titulo: titulo,
          descricao: descricao,
          categoria: "Comunicação Efetiva", // Placeholder para legacy
          prazo_implementacao: "2-4 semanas", // Placeholder para legacy
          sugestoes: [],
          impacto: '',
        };
      });
    }
  })();

  const journeyTabs = [
    { id: 'overview', label: 'Relatório', icon: <User /> },
    { id: 'action_plan', label: 'Plano de Ação', icon: <Rocket /> },
  ];

  // Função para converter IDs das dimensões em nomes legíveis
  const convertDimensionLabel = (dimensionId: string): string => {
    const dimensionMap: { [key: string]: string } = {
      'energia_social': 'Energia Social',
      'estabilidade_emocional': 'Estabilidade Emocional',
      'empatia_colaboracao': 'Empatia e Colaboração',
      'foco_disciplina': 'Foco e Disciplina',
      'criatividade_curiosidade': 'Criatividade e Curiosidade'
    };
    return dimensionMap[dimensionId] || dimensionId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const radarChartDataObject = radarChartData?.labels?.length ? {
    labels: radarChartData.labels.map(convertDimensionLabel),
    datasets: [
      {
        label: 'Sua Pontuação',
        data: radarChartData.userScores || [],
        backgroundColor: 'rgba(198, 107, 86, 0.2)', // primary-500 (terracota) com transparência
        borderColor: 'rgb(198, 107, 86)', // primary-500
        borderWidth: 2,
        pointBackgroundColor: 'rgb(198, 107, 86)',
        pointBorderColor: 'rgb(198, 107, 86)',
      },
      {
        label: `Referência: ${result.dominantProfile.name}`,
        data: radarChartData.profileScores || [],
        backgroundColor: 'rgba(138, 115, 96, 0.2)', // neutral-500 com transparência
        borderColor: 'rgb(138, 115, 96)', // neutral-500
        borderWidth: 2,
        pointBackgroundColor: 'rgb(138, 115, 96)',
        pointBorderColor: 'rgb(138, 115, 96)',
      }
    ],
  } : null;

  return (
    <div className="space-y-8">
      <header className="bg-gray-50 mt-6 rounded-lg">
        <h1 className="text-4xl font-bold text-gray-800 p-6">{dominantProfileContent.titulo}</h1>
        <p className="mt-2 text-lg text-gray-600"></p>
      </header>

      <Tabs tabs={journeyTabs}>
        {(activeTab) => {
          switch (activeTab) {
            case 'overview':
              return (
                <div className="max-w-6xl mx-auto mt-12 mb-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                    {/* Relatório em Markdown - Ocupa 2/3 do espaço */}
                    <div className="lg:col-span-2 space-y-4">
                      {dominantProfileContent.relatorio?.conteudo ? (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                          <div className="p-6 border-b border-slate-100">
                            <h2 className="text-lg font-semibold text-slate-900">Relatório de Perfil</h2>
                            <p className="text-sm text-slate-600 mt-1">Análise personalizada baseada em suas respostas</p>
                          </div>
                          <div className="p-6">
                            <MarkdownPreview 
                              source={dominantProfileContent.relatorio.conteudo}
                              style={{ backgroundColor: 'transparent' }}
                              data-color-mode="light"
                            />
                          </div>
                        </div>
                      ) : (
                        // Fallback para formato legado
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                          <div className="p-6 border-b border-slate-100">
                            <h2 className="text-lg font-semibold text-slate-900">Descrição do Perfil</h2>
                            <p className="text-sm text-slate-600 mt-1">Características principais do seu perfil</p>
                          </div>
                          <div className="p-6 space-y-4">
                            <div>
                              <h3 className="text-xl font-semibold mb-3 text-neutral-900">{dominantProfileContent.titulo}</h3>
                              <p className="text-neutral-700 leading-relaxed text-justify">{dominantProfileContent.descricao}</p>
                            </div>
                            
                            {dominantProfileContent.descricao_detalhada && (
                              <div>
                                <h4 className="text-lg font-semibold mb-2 text-neutral-900">Análise Detalhada</h4>
                                <p className="text-neutral-700 leading-relaxed text-justify">{dominantProfileContent.descricao_detalhada}</p>
                              </div>
                            )}

                            {/* Pontos Fortes */}
                            {dominantProfileContent.pontos_fortes_detalhados?.length > 0 && (
                              <div>
                                <h4 className="text-lg font-semibold mb-3 text-green-700 flex items-center">
                                  <CheckCircle className="mr-2" size={20} /> Pontos Fortes
                                </h4>
                                <div className="space-y-3">
                                  {dominantProfileContent.pontos_fortes_detalhados.map((strength: any, index: number) => (
                                    <div key={index} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                      <h5 className="font-medium text-green-800">{strength.titulo}</h5>
                                      <p className="text-sm text-green-700 mt-1">{strength.descricao}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Áreas de Desenvolvimento */}
                            {dominantProfileContent.areas_desenvolvimento?.length > 0 && (
                              <div>
                                <h4 className="text-lg font-semibold mb-3 text-orange-700 flex items-center">
                                  <Target className="mr-2" size={20} /> Áreas de Desenvolvimento
                                </h4>
                                <div className="space-y-3">
                                  {dominantProfileContent.areas_desenvolvimento.map((area: any, index: number) => (
                                    <div key={index} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                      <h5 className="font-medium text-orange-800">{area.titulo}</h5>
                                      <p className="text-sm text-orange-700 mt-1">{area.descricao}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Gráfico Radar - Ocupa 1/3 do espaço */}
                    <div className="lg:col-span-1">
                      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-fit">
                        <div className="p-6 border-b border-slate-100">
                          <h2 className="text-lg font-semibold text-slate-900">Análise do Perfil</h2>
                          <p className="text-sm text-slate-600 mt-1">Distribuição dos traços</p>
                        </div>
                        <div className="p-6">
                          <div className="h-80 w-full">
                            {radarChartDataObject && <RadarChartComponent data={radarChartDataObject} />}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            case 'action_plan':
              return <ActionPlanSection strategies={actionPlanStrategies} />;
            default:
              return null;
          }
        }}
      </Tabs>
    </div>
  );
};